import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { distinctUntilChanged, filter, from, map, merge, of } from 'rxjs';

import { authorizationResponse, AuthEvent, takePendingAuthorization } from '@ninety/mobile/platform/auth';
import { NetworkService } from '@ninety/mobile/platform/services';

import { capacitorAppActions } from '../../capacitor/app/capacitor-app.actions';

import { BrowserLocation } from './browser-location';
import { purgeLegacyCredentials } from './legacy-credentials';
import { SessionActions } from './session.actions';

/**
 * The app's own lifecycle, as auth events: the launch that starts a Session Restore, and the
 * resume that renews one.
 *
 * **There is no timer here, and there must not be one.** The design has three demand-driven
 * refresh triggers and no schedule — pre-flight, a reactive 401, and resume. A timer would be a
 * fourth source of truth about when a token is due, and definitions of that which disagree are what
 * force people to sign in again.
 */
@Injectable()
export class SessionLifecycleEffects {
  private readonly actions$ = inject(Actions);
  private readonly location = inject(BrowserLocation);
  private readonly network = inject(NetworkService);

  /**
   * Decide what this launch is, and say so once.
   *
   * Two possibilities, and exactly one of them may happen:
   *
   * 1. **A cold start**, which reads the launch state. That is the one Command in the app no
   *    transition returns, and it cannot be otherwise — the machine *starts* in
   *    `restoringSession{readingLaunchState}`, so the state that would have produced it is the
   *    initial state rather than the result of an event. Issuing it as a Command anyway keeps
   *    `CommandRunner` the only thing that reaches the device.
   * 2. **A redirect coming back**, on the web build, where a federated sign-in navigated the tab away
   *    and the response arrived in a new page. The Grant is restored from where it was persisted and
   *    handed to the machine, which validates the nonce exactly as it does on native.
   *
   * **The two are one decision on purpose.** Split across two effects, whichever ran first would
   * consume the stored Grant and the other would either file a false `auth.illegal_transition` —
   * `launchStateRead` arriving while the machine is already `authorizing` — or, worse, leave a launch
   * with a callback URL and no Grant sitting in `restoringSession` behind a splash screen with
   * nothing to move it on.
   *
   * `of` emits once and completes, so this cannot fire twice however many times it is subscribed
   * within a launch.
   */
  readonly launch$ = createEffect(() => of(this.launchAction()));

  /**
   * The resumed Grant, or the Command that starts an ordinary launch.
   *
   * The pending Grant is *taken* — removed as it is read — which is what stops a callback URL that
   * gets reloaded, bookmarked or pasted somewhere from being redeemed twice. It is only taken once
   * the URL has been recognised as a callback, so an unrelated reload of the sign-in page does not
   * consume a redirect that is still in flight.
   */
  private launchAction(): Action {
    const params = authorizationResponse(this.location.href());
    const pending = params ? takePendingAuthorization() : null;

    if (params && pending) {
      // `issuedAt` comes from the persisted Grant, so the five-minute freshness bound is measured
      // from when the request was sent rather than from when this page happened to start.
      const event: AuthEvent = { type: 'authorizationResumed', ...pending, params };
      return SessionActions.eventReceived({ event });
    }

    // Includes a callback URL with no Grant behind it — a stale link, or a tab that was closed and
    // reopened. Nothing to resume, so this is an ordinary launch.
    return SessionActions.commandIssued({ command: { do: 'readLaunchState' } });
  }

  /**
   * Remove the legacy credentials left in device storage, once per launch.
   *
   * Deleting the code that wrote a plaintext password does not delete the password. See
   * {@link purgeLegacyCredentials}. It runs unconditionally rather than behind a "have I already
   * done this?" flag, because the flag would be one more thing persisted to get wrong and the work
   * is a handful of key deletes.
   *
   * Independent of the restore above and deliberately not sequenced with it: none of these keys is
   * read by anything any more, so nothing waits on this and a slow store cannot delay the launch.
   */
  readonly purgeLegacyCredentials$ = createEffect(() => from(purgeLegacyCredentials()), { dispatch: false });

  /**
   * Resume is the moment a token is most likely stale, and it is the only moment a backgrounded app
   * can act on: iOS suspends WebView timers while backgrounded, so anything scheduled is asleep for
   * exactly the period that makes a session go stale.
   */
  readonly refreshOnResume$ = createEffect(() =>
    this.actions$.pipe(
      ofType(capacitorAppActions.appResumed),
      map(() => SessionActions.eventReceived({ event: { type: 'refreshRequested', trigger: 'appResumed' } }))
    )
  );

  /**
   * The network coming back — or somebody asking — as an auth event.
   *
   * The counterpart to {@link refreshOnResume$}: **this asks every time and checks nothing.** Whether there is a
   * session, whether one is already being restored, whether a token is even due — none of that is decided here.
   * `transition()` decides: a deferred restore restarts, an authorized session refreshes only if its token is
   * inside the refresh window, and every other state answers with a matched "do nothing".
   *
   * `networkStatus$` is a `BehaviorSubject` seeded `connected: true`, so a subscriber receives one
   * emission before the OS has been asked. It is deliberately not skipped: it arrives while the
   * machine is in `restoringSession`, where `refreshRequested` is documented as silent, so the cost
   * of the honest version is nothing and the alternative is a `skip(1)` that would swallow a real
   * reconnection on a launch that started offline.
   *
   * **`retries$` is merged in rather than folded into the boolean, and that is what makes the Retry
   * button work.** A cold start whose request went out and failed — a 5xx, a timeout — defers with
   * the device online the whole time, so `distinctUntilChanged` swallows the re-read `true` and the OS
   * fires nothing, because nothing changed. Relaxing the dedup instead would restore-storm on a
   * flapping connection. Two facts, two streams, and `retryRequested` says which happened.
   */
  readonly refreshOnConnectivity$ = createEffect(() =>
    merge(
      this.network.networkStatus$.pipe(
        map(status => status.connected),
        // so a Wi-Fi-to-cellular handover is not a reconnection
        distinctUntilChanged(),
        filter(connected => connected),
        map(() => 'connectivityRegained' as const)
      ),
      this.network.retries$.pipe(map(() => 'retryRequested' as const))
    ).pipe(map(trigger => SessionActions.eventReceived({ event: { type: 'refreshRequested', trigger } })))
  );
}
